/**
 * Single source of truth for detecting which e-commerce/inventory platform a raw
 * connection record belongs to.
 *
 * INC-11 history: this exact "is this connection Shopify/Amazon/Veeqo" check used to be
 * copy-pasted inline across ~7 different files (and re-copy-pasted into their tests), which
 * let Veeqo silently disappear from one UI surface at a time as each was fixed
 * independently. Import from here instead of re-implementing the check, and have tests
 * import the same functions rather than duplicating them. `src/shared/connectionPlatform.ts`
 * re-exports this module, so the client and the edge functions run the same code.
 *
 * The decision this file encodes (UC-121/UC-126): Veeqo is a *pooled warehouse inventory truth*
 * source, not an independently reportable sales connection. It therefore appears as a selectable
 * inventory Data Source and overlays Stock Held onto Shopify/Amazon report rows, but never gets a
 * SKU/report row of its own — `generate-sku-report-v6` has no way to build one, since Veeqo order
 * data is explicitly out of scope.
 */

import { isInventorySnapshotConnectionId } from './inventoryCsvConnectionChannels.ts';
import type { AmazonAccountType } from '../../../src/shared/types/amazonAccount.ts';

export { INVENTORY_SNAPSHOT_ID_RE } from './inventoryCsvConnectionChannels.ts';

export type { AmazonAccountType };

export type EcommercePlatform = 'shopify' | 'amazon' | 'veeqo';

/**
 * Platforms whose connections can produce their own SKU/report row via generate-sku-report-v6.
 * Veeqo is deliberately excluded: it's a pooled warehouse-inventory-truth source that overlays
 * Stock Held onto existing Shopify/Amazon connections (see
 * connectionMatchesInventoryPrimarySource in supabase/functions/_shared/skuReportGenerator.ts)
 * rather than being an independently-reportable connection with its own sales data.
 */
export const REPORTABLE_ECOMMERCE_PLATFORMS: readonly EcommercePlatform[] = ['shopify', 'amazon'];

/**
 * Platforms displayable as selectable "Data Sources" on inventory surfaces (e.g. the
 * Current Stock tab), where Veeqo legitimately has its own snapshot-sourced rows.
 */
export const INVENTORY_DISPLAY_PLATFORMS: readonly EcommercePlatform[] = ['shopify', 'amazon', 'veeqo'];

export interface ConnectionPlatformInput {
  type?: string | null;
  service?: string | null;
  connector_name?: string | null;
}

/** True if a connection record represents a CSV import (as opposed to a live Fivetran sync). */
export function isCsvImportConnection(connection: ConnectionPlatformInput): boolean {
  const type = connection.type?.toLowerCase() || '';
  const service = connection.service?.toLowerCase() || '';
  return type === 'csv' || service === 'csv';
}

/**
 * Detects which live e-commerce/inventory platform a raw connection record belongs to.
 *
 * `type` is the authoritative field: `get-data-linked-connectors` returns Fivetran's raw
 * `GET /v1/connectors/{id}` payload, which carries no `type`, so `connectorApi`'s
 * `type: item.type || item.service` always resolves `type` to the connector slug (real
 * records store `type: 'shopify'` / `'amazon_selling_partner'` / `'veeqo'`). Fivetran's
 * product-category strings (`'Sales'` etc.) come from the separate
 * `/metadata/connector-types` catalog used to render the connector picker and are never
 * persisted onto a connection.
 *
 * `service` and `connector_name` are consulted only when the higher-precedence field is
 * absent, covering older records saved before the connector refresh populated `type`. They
 * are deliberately not OR'd in: a record whose `type` names a different platform must not be
 * reclassified by a stale `service`, and the backend snapshot/NERV gates key on `type` alone.
 *
 * Returns null for CSV-import connections (use `isCsvImportConnection` /
 * `getCsvEcommerceSubtype` for those) and for any unsupported platform.
 */
export function detectEcommercePlatform(connection: ConnectionPlatformInput): EcommercePlatform | null {
  if (isCsvImportConnection(connection)) return null;

  const slug = (
    connection.type
    || connection.service
    || connection.connector_name
    || ''
  ).toLowerCase();

  if (slug.includes('shopify')) return 'shopify';
  if (slug.includes('amazon')) return 'amazon';
  if (slug.includes('veeqo')) return 'veeqo';
  return null;
}

/** True if this live connection can produce its own SKU/report row. Veeqo always returns false. */
export function isReportablePlatformConnection(connection: ConnectionPlatformInput): boolean {
  const platform = detectEcommercePlatform(connection);
  return platform !== null && (REPORTABLE_ECOMMERCE_PLATFORMS as readonly string[]).includes(platform);
}

export interface CsvConnectionInput {
  import_type?: string | null;
  ecommerce_subtype?: string | null;
}

/**
 * Returns the e-commerce platform a CSV-import connection represents (Shopify/Amazon only --
 * Veeqo has no CSV-import variant), or null if it's not a recognized ecommerce CSV import.
 */
export function getCsvEcommerceSubtype(connection: CsvConnectionInput): 'shopify' | 'amazon' | null {
  if (connection.import_type !== 'ecommerce') return null;
  const subtype = connection.ecommerce_subtype?.toLowerCase();
  return subtype === 'shopify' || subtype === 'amazon' ? subtype : null;
}

/**
 * True if this connection can produce its own SKU/report row (see
 * REPORTABLE_ECOMMERCE_PLATFORMS) -- either a live Shopify/Amazon connection, or a CSV import
 * with a Shopify/Amazon ecommerce subtype. Veeqo connections always return false here.
 */
export function isReportableConnection(
  connection: ConnectionPlatformInput & CsvConnectionInput
): boolean {
  if (detectEcommercePlatform(connection) !== null) return isReportablePlatformConnection(connection);
  return getCsvEcommerceSubtype(connection) !== null;
}

/**
 * True if this connection should be offered as a selectable SKU Reports data source.
 *
 * This is `isReportableConnection` plus one Reports-surface-specific exclusion: inventory-only
 * CSV imports, identified by structured `_inventory_amazon_` / `_inventory_shopify_` /
 * `_inventory_YYYYMMDD` markers in their connection id. Those imports
 * inherit import_type/ecommerce_subtype from their parent connection, so they look reportable;
 * including them would query the same sales table twice and double GMV.
 */
export function isReportsSelectableConnection(
  connection: ConnectionPlatformInput & CsvConnectionInput & { id?: string | null; connectionId?: string | null }
): boolean {
  const connectionId = connection.id || connection.connectionId || '';
  if (isInventorySnapshotConnectionId(connectionId)) return false;
  return isReportableConnection(connection);
}

/**
 * True if this connection should be offered as a selectable inventory "Data Source" (Current
 * Stock tab and similar surfaces), where Shopify, Amazon, and Veeqo (but not Veeqo CSV, which
 * doesn't exist) are all valid.
 */
export function isInventoryDisplayableConnection(connection: ConnectionPlatformInput): boolean {
  const platform = detectEcommercePlatform(connection);
  return platform !== null && (INVENTORY_DISPLAY_PLATFORMS as readonly string[]).includes(platform);
}

export function readStoredAmazonAccountType(
  connection: { amazon_account_type?: unknown } | null | undefined,
): AmazonAccountType | null {
  const value = connection?.amazon_account_type;
  return value === 'seller' || value === 'vendor' ? value : null;
}

/**
 * Vendor only when a vendor daily report exists and Seller FBA inventory does not.
 * Seller inventory present stays Seller, including a dest that has both.
 * Neither shape is unresolved — callers must not fall back to Seller SQL.
 */
export function classifyAmazonAccountFromTables(tables: {
  hasVendorDailyReport: boolean;
  hasSellerInventory: boolean;
}): AmazonAccountType | null {
  if (tables.hasSellerInventory) return 'seller';
  if (tables.hasVendorDailyReport) return 'vendor';
  return null;
}

export function classifyAmazonAccountFromTableNames(tableNames: Iterable<string>): AmazonAccountType | null {
  const names = new Set(tableNames);
  return classifyAmazonAccountFromTables({
    hasVendorDailyReport:
      names.has('vendor_inventory_sourcing_retail_asin_report_daily')
      || names.has('vendor_sales_sourcing_retail_asin_report_daily'),
    hasSellerInventory: names.has('fba_inventory_summary'),
  });
}

/**
 * Fills a missing stored account type from a live connector list (Fivetran `is_vendor`
 * or an already resolved type). A stored value is left as-is.
 */
export function connectionsWithAmazonAccountTypes<
  T extends { id?: string | null; connectionId?: string | null; connectorId?: string | null; amazon_account_type?: unknown },
>(
  connections: T[],
  discovered: Array<{ id?: string | null; connectionId?: string | null; connectorId?: string | null; amazon_account_type?: unknown }>,
): T[] {
  const byId = new Map<string, AmazonAccountType>();
  for (const item of discovered) {
    const accountType = readStoredAmazonAccountType(item);
    if (!accountType) continue;
    for (const id of [item.id, item.connectionId, item.connectorId]) {
      if (typeof id === 'string' && id.length > 0) byId.set(id, accountType);
    }
  }
  return connections.map((connection) => {
    if (readStoredAmazonAccountType(connection)) return connection;
    for (const id of [connection.id, connection.connectionId, connection.connectorId]) {
      if (typeof id !== 'string' || id.length === 0) continue;
      const accountType = byId.get(id);
      if (accountType) return { ...connection, amazon_account_type: accountType };
    }
    return connection;
  });
}

/**
 * True when every live ecommerce connection in the selection is Vendor Central.
 * Used to show seller-only report screens as not available.
 */
export function isVendorOnlySelection(
  connections: Array<ConnectionPlatformInput & { id?: string | null; connectionId?: string | null; amazon_account_type?: unknown }>,
  selectedIds: string[],
): boolean {
  const selected = selectedIds.length === 0
    ? connections
    : connections.filter((connection) =>
      selectedIds.includes(connection.id || '') || selectedIds.includes(connection.connectionId || ''),
    );
  const live = selected.filter((connection) => detectEcommercePlatform(connection) !== null);
  return live.length > 0 && live.every((connection) => readStoredAmazonAccountType(connection) === 'vendor');
}
